/* LUCRÜ™ — © 2026 LUCRÜ. All rights reserved. */
import { useEffect, useState } from "react";
import { PlugIcon, PlugsConnectedIcon } from "@phosphor-icons/react";
import { api } from "@/lib/api";

const ConnectPanel = ({ connected, channel, onConnect, onDisconnect, onTryDemo }) => {
  const [platforms, setPlatforms] = useState([]);
  const [platform, setPlatform] = useState("twitch");
  const [input, setInput] = useState(channel || "");

  useEffect(() => {
    api.get("/platforms").then((r) => setPlatforms(r.data)).catch(() => {});
  }, []);

  const submit = (e) => {
    e.preventDefault();
    const backendPlatforms = new Set(["twitch", "kick", "youtube"]);
    if (!input.trim() && backendPlatforms.has(platform)) return;
    onConnect(platform, input.trim() || platform);
  };

  const active = platforms.find((p) => p.id === platform);
  const isRelayMode = active?.mode === "relay";
  const placeholderMap = {
    twitch: "e.g. shroud",
    kick: "e.g. adinross",
    youtube: "video id or https://youtube.com/watch?v=...",
  };
  const labelMap = {
    twitch: "Channel name",
    kick: "Kick channel slug",
    youtube: "YouTube live video id or URL",
  };

  return (
    <div className="glass rounded-2xl p-5">
      <div className="flex items-center justify-between mb-4">
        <div className="font-display font-black text-sm uppercase tracking-widest">Connect</div>
        <PlugIcon size={16} color="#00E5FF" />
      </div>

      <div className="grid grid-cols-3 gap-1.5 mb-4" data-testid="platform-grid">
        {platforms.map((p) => (
          <button
            key={p.id}
            onClick={() => setPlatform(p.id)}
            disabled={p.status === "soon"}
            data-testid={`platform-select-${p.id}`}
            className={`text-[10px] py-2 px-1 rounded-lg border transition-colors ${
              platform === p.id
                ? "border-[#00E5FF] text-[#00E5FF] bg-[#00E5FF]/5"
                : "border-[#222233] text-white/60 hover:border-white/40"
            } ${p.status === "soon" ? "opacity-40 cursor-not-allowed" : ""}`}
            title={p.description}
          >
            {p.name}
          </button>
        ))}
      </div>

      {!connected ? (
        <form onSubmit={submit}>
          <label className="text-xs text-white/50 mb-2 block">
            {labelMap[platform] || "Your handle / room (optional)"}
          </label>
          <input
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder={placeholderMap[platform] || "e.g. your_username"}
            data-testid="channel-input"
            className="w-full bg-[#0F0F16] border border-[#222233] rounded-lg px-3 py-2.5 text-sm outline-none focus:border-[#00E5FF] transition-colors"
          />
          {isRelayMode && (
            <div className="mt-2 text-[10px] text-white/50 leading-relaxed bg-[#00E5FF]/5 border border-[#00E5FF]/20 rounded-lg p-2">
              Mobile relay mode: after connecting, share your relay link with yourself (on your streaming phone) or a trusted mod. They type the chat messages, you hear them live.
            </div>
          )}
          <button type="submit" data-testid="connect-button" className="btn-pill btn-primary w-full mt-3 justify-center">
            <PlugsConnectedIcon size={16} weight="fill" /> Connect
          </button>
          {onTryDemo && (
            <button
              type="button"
              onClick={onTryDemo}
              data-testid="try-demo-chat"
              className="w-full mt-2 text-[11px] text-[#39FF14] hover:text-[#5CFF3C] py-1"
            >
              or try a demo chat first →
            </button>
          )}
        </form>
      ) : (
        <div>
          <div className="bg-[#0F0F16] border border-[#222233] rounded-lg px-3 py-2.5 text-sm flex items-center justify-between">
            <span className="text-[#39FF14] truncate">#{channel}</span>
            <span className="w-2 h-2 rounded-full bg-[#39FF14] animate-pulse" />
          </div>
          <button
            onClick={onDisconnect}
            data-testid="disconnect-button"
            className="btn-pill btn-ghost w-full mt-3 justify-center"
          >
            Disconnect
          </button>
        </div>
      )}
    </div>
  );
};

export default ConnectPanel;
